/**
 * EL CALENDARIO DEL NEGOCIO — NIVEL 2.  (US4, FR-024)
 *
 * *"ver su calendario embebido en el dashboard, donde podrá filtrar por
 * trabajador, horario, tipo de servicio, por día"*.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  LA REGLA DE ESTA PANTALLA:                                               ║
 * ║  SI LA AGENDA NO RESPONDE, **NO SE DIBUJA LA REJILLA**.                   ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Una rejilla vacía y una agenda caída se ven exactamente igual: un día sin
 * citas. Y el dueño de la clínica no concluye "esto está roto", concluye "hoy no
 * tengo pacientes". Es la peor versión del fallo — no parece un error, parece un
 * dato. Por eso, cuando `agenda.alcanzable` es `false`, aquí se pinta el motivo y
 * NADA MÁS.
 */
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'

import { useFichaNegocio } from '../lib/ficha-negocio'
import { leerDiaCalendario, leerIncluirPasadas, leerVistaCalendario, recordarCalendario, recordarIncluirPasadas } from '../lib/calendario-memoria'
import { useIncluirTestsVista } from '../lib/use-incluir-tests'
import { useSoloLectura } from '../auth'
import { useEsAdminUi } from '../ver-como'

import { api } from '../lib/api'
import { contarEnVentana, useRelojPared, ZONA_DEFECTO } from '../lib/citas-vigentes'
import { columnasConVigentes, vigentesPorProfesional } from './calendario-cuenta'
import { RejillaCalendario, type BloqueoCalendario, type Columna } from './calendario-rejilla'
import { CalendarioMes } from './calendario-mes'
import { DialogoNuevaCita, type HuecoElegido } from './calendario-crear'
import { aplicar, conPropuesta } from './calendario-propuestas'
import { usarPropuestas } from './usar-propuestas'
import { BarraCalendario } from './calendario-barra'
import { NavegacionCalendario } from './calendario-navegacion'
import { FilaProfesionalesCalendario } from './calendario-profesionales'
import { diaCerradoDelCentro, seOcultaElDia, type PeriodoCierre } from './calendario-cerrados'
import {
    horarioDeVariosDias,
    horarioVisible,
    mesDe,
    semanaDe,
    type PlanSemanal,
} from './calendario-geometria'

interface Cita {
    id: number
    /** `YYYY-MM-DD`. Con rango, hace falta para saber en qué columna va. */
    dia: string
    inicio: string
    fin: string
    minutos: number
    profesionalId: number | null
    servicioId: number | null
    esTest: boolean
    cliente: { nombre: string; apellidos: string; telefono: string } | null
}

interface Calendario {
    accountId: number
    dia: string
    agenda: { alcanzable: boolean; motivo: string | null }
    citas: Cita[]
    desde: string
    hasta: string
    recortado: boolean
    incompleto: boolean
    zonaHoraria: string
    profesionales: { id: number; nombre: string; servicioIds: number[]; plan: PlanSemanal; color: string }[]
    servicios: { id: number; nombre: string; duracionMin: number | null }[]
    laboratorio: { citas: number; incluidas: boolean }
    bloqueos: { alcanzable: boolean; lista: BloqueoCalendario[] }
}

export function CalendarioNegocio() {
    const { accountId } = useParams<{ accountId: string }>()
    // El día y la vista se pueden fijar por URL. Es lo que permite enlazar aquí
    // desde el registro de movimientos: «llévame al calendario de ESE día».
    const [params, setParams] = useSearchParams()
    const [dia, setDia] = useState<string>(() => leerDiaCalendario(params, accountId))
    // FR-021 · se marcan y desmarcan, no se elige uno de un desplegable.
    //
    // Se guarda lo OCULTO y no lo visible a propósito: así, cuando el negocio dé
    // de alta a alguien nuevo, aparece encendido sin que nadie tenga que
    // acordarse de marcarlo. Con la lista de visibles, el recién llegado sería
    // invisible y nadie sabría por qué.
    // FR-024 · lo que se ha movido en pantalla y todavía NO se ha escrito.
    /** Por qué un clic en la rejilla no ha abierto el alta. Ver `alPinchar`. */
    const [avisoCrear, setAvisoCrear] = useState<string | null>(null)
    const { propuestas, setPropuestas, guardando, fallos, setFallos, guardarPropuestas } =
        usarPropuestas(accountId)

    // FR-012 · el servicio elegido PARA CREAR. Es distinto del filtro: filtrar
    // dice qué se ve, esto dice qué se va a reservar. Mezclarlos haría que ocultar
    // un servicio armara el modo de crear sin querer.
    const [servicioParaCrear, setServicioParaCrear] = useState<number | null>(null)
    const [hueco, setHueco] = useState<HuecoElegido | null>(null)

    const [profesOcultos, setProfesOcultos] = useState<Set<number>>(new Set())
    const [serviciosOcultos, setServiciosOcultos] = useState<Set<number>>(new Set())

    const alternar = (s: Set<number>, id: number): Set<number> => {
        const n = new Set(s)
        if (n.has(id)) n.delete(id)
        else n.add(id)
        return n
    }
    const { esDemo } = useFichaNegocio(accountId)
    const esAdminUi = useEsAdminUi()
    const soloLectura = useSoloLectura()
    const { incluirTests, cambiar: setIncluirTests, mostrarInterruptor, listo } = useIncluirTestsVista(accountId, esDemo, esAdminUi)

    // FR-006/FR-007 · las DOS disposiciones, para compararlas con datos delante.
    // Es una preferencia de quien opera, no un estado de la pantalla, así que se
    // recuerda. Cuando el propietario elija, la que sobre SE RETIRA: dos formas
    // de hacer lo mismo son dos sitios donde arreglar el mismo fallo.
    // FR-008/FR-011 · día · semana · mes. Cambiar de vista NO cambia lo que se
    // está mirando: de la semana del 12 al mes, se va al mes que la contiene.
    const [vista, setVista] = useState<'dia' | 'semana' | 'mes'>(() => leerVistaCalendario(params, accountId))

    useEffect(() => {
        if (!accountId) return
        recordarCalendario(accountId, vista, dia)
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                next.set('vista', vista)
                next.set('dia', dia)
                return next.toString() === prev.toString() ? prev : next
            },
            { replace: true },
        )
    }, [accountId, vista, dia, setParams])

    const CLAVE_DISPOSICION = `calendario:disposicion:${accountId}`
    const [disposicion, setDisposicion] = useState<'columnas' | 'juntos'>(() => {
        const v = localStorage.getItem(CLAVE_DISPOSICION)
        return v === 'juntos' ? 'juntos' : 'columnas'
    })
    function elegirDisposicion(v: 'columnas' | 'juntos') {
        setDisposicion(v)
        localStorage.setItem(CLAVE_DISPOSICION, v)
    }

    const CLAVE_CERRADOS = `calendario:ocultarCerrados:${accountId}`
    const [ocultarCerrados, setOcultarCerrados] = useState(() => {
        return localStorage.getItem(CLAVE_CERRADOS) === '1'
    })
    function alternarCerrados() {
        setOcultarCerrados((v) => {
            const siguiente = !v
            localStorage.setItem(CLAVE_CERRADOS, siguiente ? '1' : '0')
            return siguiente
        })
    }

    const [incluirPasadas, setIncluirPasadas] = useState(() => leerIncluirPasadas(accountId))
    function elegirIncluirPasadas(v: boolean) {
        setIncluirPasadas(v)
        if (accountId) recordarIncluirPasadas(accountId, v)
    }

    // FR-010 · una petición por vista, no una por día: los filtros de rango de
    // la agenda aceptan un intervalo.
    const diasSemana = semanaDe(dia)
    // El mes pide las 42 celdas —seis semanas— y no del 1 al 31: los días de
    // relleno del principio y del final también se pintan, y si no se pidieran
    // saldrían vacíos aunque tuvieran citas.
    const celdasMes = mesDe(dia)
    const rango =
        vista === 'semana'
            ? { desde: diasSemana[0] as string, hasta: diasSemana[6] as string }
            : vista === 'mes'
              ? { desde: celdasMes[0] as string, hasta: celdasMes[41] as string }
              : { desde: dia, hasta: dia }

    const qs = new URLSearchParams({
        desde: rango.desde,
        hasta: rango.hasta,
        incluirTests: incluirTests ? '1' : '0',
    })

    const { data, isPending, isError, isFetching, refetch } = useQuery<Calendario>({
        queryKey: ['calendario', accountId, vista, rango.desde, rango.hasta, incluirTests],
        queryFn: () => api.get<Calendario>(`/business/${accountId}/calendar?${qs.toString()}`),
        enabled: Boolean(accountId) && listo,
        // ── EL PARPADEO AL CAMBIAR DE DÍA O DE SEMANA ────────────────────────
        //
        // Cada flecha cambia la clave de la consulta, y sin esto los datos
        // anteriores se DESCARTAN: la rejilla se desmonta, la pantalla se queda
        // en «consultando», y vuelve a montarse cuando llega lo nuevo. Con dos
        // clics seguidos eso es un parpadeo continuo.
        //
        // `keepPreviousData` deja la semana anterior en pantalla mientras llega
        // la siguiente. Lo que se ve durante ese instante es real —era la semana
        // de antes—, y se avisa con `isFetching` en la banda para que nadie
        // confunda lo viejo con lo nuevo.
        placeholderData: (anterior) => anterior,
    })
    const { data: vacaciones, refetch: refetchVac } = useQuery<{ vacaciones: PeriodoCierre[] }>({
        queryKey: ['vacaciones', accountId],
        queryFn: () => api.get<{ vacaciones: PeriodoCierre[] }>(`/business/${accountId}/agenda/vacaciones`),
        enabled: Boolean(accountId),
    })

    const recargar = useCallback(() => {
        void refetch()
        void refetchVac()
    }, [refetch, refetchVac])

    const nombreProf = (id: number | null): string =>
        data?.profesionales.find((p) => p.id === id)?.nombre ?? (id === null ? 'Sin asignar' : `Profesional ${id}`)
    const colorDe = (id: number | null): string =>
        data?.profesionales.find((p) => p.id === id)?.color ?? '#64748b'

    const nombreServ = (id: number | null): string =>
        data?.servicios.find((s) => s.id === id)?.nombre ?? (id === null ? 'Sin servicio' : `Servicio ${id}`)

    const profesVisibles = (data?.profesionales ?? []).filter((p) => !profesOcultos.has(p.id))

    // Las citas que se pintan. Se filtra AQUÍ y no en la API: el rango ya está
    // pedido entero, así que marcar y desmarcar no vuelve a pedir nada — y con
    // una petición por clic la pantalla parpadearía en cada marca.
    //
    // `null` pasa siempre: una cita sin profesional o sin servicio asignado es
    // un dato que hay que ver, y esconderla al filtrar la haría desaparecer sin
    // que nadie supiera que existe.
    // Las propuestas se aplican ANTES de filtrar: si alguien mueve una cita a
    // otro profesional y ese profesional está apagado, la cita desaparece — y
    // eso es correcto, es coherente con el filtro. Al revés, se pintaría en la
    // columna de quien ya no la tiene.
    const citasConPropuestas = aplicar(data?.citas ?? [], propuestas)

    const citasVisibles = citasConPropuestas.filter(
        (c) =>
            (c.profesionalId === null || !profesOcultos.has(c.profesionalId)) &&
            (c.servicioId === null || !serviciosOcultos.has(c.servicioId)),
    )
    const hayFiltro = profesOcultos.size > 0 || serviciosOcultos.size > 0
    const reloj = useRelojPared(data?.zonaHoraria ?? ZONA_DEFECTO)
    const citasTrasServicio = citasConPropuestas.filter(
        (c) => c.servicioId === null || !serviciosOcultos.has(c.servicioId),
    )
    const totalVigentes = data?.agenda.alcanzable
        ? contarEnVentana(citasVisibles, reloj, incluirPasadas)
        : null
    const vigentesPorProf = vigentesPorProfesional(
        data?.profesionales ?? [],
        citasTrasServicio,
        reloj,
        incluirPasadas,
    )

    /*
     * La duración del servicio elegido para crear (FR-012).
     *
     * Sale del CATÁLOGO y no de un número escrito a mano: es lo que hace que el
     * resaltado mida lo que va a durar la cita de verdad. Un servicio sin
     * duración declarada no puede iluminar nada honesto, así que no arma el modo
     * de crear — FR-013.
     */
    const duracionParaCrear =
        servicioParaCrear === null
            ? undefined
            : ((data?.servicios.find((x) => x.id === servicioParaCrear)?.duracionMin ?? undefined) as
                  | number
                  | undefined)

    // Los bloqueos siguen el filtro de profesional —son de uno concreto— pero NO
    // el de servicio: un hueco retenido todavía no tiene servicio elegido, así
    // que filtrarlo por servicio lo escondería siempre.
    const bloqueosVisibles = (data?.bloqueos.lista ?? []).filter(
        (b) => !profesOcultos.has(b.profesionalId),
    )
    const bloqueosDe = (d: string, profId?: number) =>
        bloqueosVisibles.filter((b) => b.dia === d && (profId === undefined || b.profesionalId === profId))

    // ── De qué hora a qué hora se pinta (FR-002, FR-008) ────────────────────
    //
    // En semana hace falta UNA sola franja para los siete días: si cada columna
    // empezara a su hora, las rayas no cuadrarían entre columnas y la pantalla
    // dejaría de leerse de un vistazo, que es para lo que sirve una semana.
    const planes = (data?.profesionales ?? []).map((p) => p.plan)
    const citasDe = (d: string) => citasVisibles.filter((c) => c.dia === d)
    const cierres = vacaciones?.vacaciones ?? []
    const gente = data?.profesionales ?? []
    const diaCerrado = (d: string) => diaCerradoDelCentro(gente, cierres, d)

    const horario =
        vista === 'semana'
            ? horarioDeVariosDias(
                  planes,
                  diasSemana,
                  Object.fromEntries(diasSemana.map((d) => [d, citasDe(d)])),
              )
            : horarioVisible(planes, dia, citasVisibles)

    // ── Las columnas (FR-004, FR-006, FR-008) ───────────────────────────────
    //
    // Las tres particiones se construyen SOBRE LA MISMA rejilla: lo único que
    // cambia es qué es una columna —un profesional, un día, o todo junto—.

    let columnas: Columna[]
    if (vista === 'semana') {
        // En semana, cada columna es un día. Con varios profesionales no caben
        // las dos particiones a la vez, así que se ven mezclados o se filtra a
        // uno con el desplegable que ya existe (FR-008).
        columnas = diasSemana
            .filter((d) => !seOcultaElDia(ocultarCerrados, diaCerrado(d), citasDe(d).length > 0))
            .map((d) => {
                const delDia = citasDe(d)
                return {
                    clave: d,
                    titulo: new Date(`${d}T12:00:00Z`).toLocaleDateString('es-ES', { weekday: 'short' }),
                    subtitulo: new Date(`${d}T12:00:00Z`).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                    }),
                    citas: delDia,
                    bloqueos: bloqueosDe(d),
                    dia: d,
                    apagada: diaCerrado(d),
                }
            })
    } else if (disposicion === 'columnas' && profesVisibles.length > 0) {
        columnas = profesVisibles.map((p) => ({
            clave: String(p.id),
            titulo: p.nombre || `Profesional ${p.id}`,
            citas: citasVisibles.filter((c) => c.profesionalId === p.id),
            bloqueos: bloqueosDe(dia, p.id),
            dia,
            // Sin esto, soltar una cita en la columna de otro profesional solo le
            // cambiaba la hora: al guardar reaparecía en la columna de siempre. Y
            // si se soltaba a la misma hora, la propuesta salía «inmóvil» y el
            // arrastre no hacía absolutamente nada, sin un aviso.
            profesionalId: p.id,
        }))
    } else {
        columnas = [
            { clave: 'todos', titulo: 'Todos', citas: citasVisibles, bloqueos: bloqueosDe(dia), dia },
        ]
    }
    columnas = columnasConVigentes(columnas, reloj, dia, incluirPasadas)

    return (
        <div className="space-y-4">
            {/* El rótulo del periodo ya NO va aquí: se ha ido con las flechas, a
                la barra que está justo encima de la rejilla. Tenerlo arriba del
                todo obligaba a mirar a dos sitios —el título, para saber qué día
                es; la barra, para cambiarlo— y son la misma pregunta. */}
            <h2 className="text-lg font-semibold text-neutral-100">Calendario</h2>

            <BarraCalendario
                disposicion={disposicion}
                elegirDisposicion={elegirDisposicion}
                variasColumnas={vista === 'dia' && profesVisibles.length > 1}
                propuestas={propuestas.size}
                guardando={guardando}
                alGuardar={() => void guardarPropuestas()}
                alDescartar={() => {
                    setPropuestas(new Map())
                    setFallos([])
                }}
                incluirTests={incluirTests}
                setIncluirTests={setIncluirTests}
                mostrarIncluirTests={mostrarInterruptor}
                mostrarNuevaCita={!soloLectura && Boolean(data?.agenda.alcanzable)}
                servicios={data?.servicios ?? []}
                servicioParaCrear={servicioParaCrear}
                alElegirServicio={setServicioParaCrear}
                {...(vista === 'mes' ? { hintCrear: 'entra en un día para elegir la hora' } : {})}
                serviciosOcultos={serviciosOcultos}
                alAlternarServ={(id) => setServiciosOcultos((s) => alternar(s, id))}
                alVerTodosServ={() => setServiciosOcultos(new Set())}
                alActualizar={recargar}
                actualizando={isFetching}
                mostrarOcultarCerrados={vista === 'semana' || vista === 'mes'}
                ocultarCerrados={ocultarCerrados}
                alAlternarCerrados={alternarCerrados}
                incluirPasadas={incluirPasadas}
                setIncluirPasadas={elegirIncluirPasadas}
            />

            {hueco && accountId && !soloLectura && (
                <DialogoNuevaCita
                    accountId={accountId}
                    hueco={hueco}
                    profesionales={profesVisibles.map((p) => ({
                        id: p.id,
                        nombre: p.nombre,
                        servicioIds: p.servicioIds,
                    }))}
                    nombreProf={nombreProf}
                    nombreServ={nombreServ}
                    alCerrar={() => setHueco(null)}
                />
            )}

            {/* ── LA FILA DE ENCIMA DE LA REJILLA ─────────────────────────────
                Tres partes, y el reparto es el que pidió el propietario: los
                avisos a la izquierda, a la misma altura que las fechas, y la
                navegación en el centro.

                Antes los avisos iban en su propia línea, debajo, y empujaban la
                rejilla hacia abajo cada vez que había algo que decir. Ahora
                ocupan el hueco que ya existía a la izquierda —que estaba vacío—
                así que no le quitan sitio a nada.

                Las tres columnas miden lo mismo (`flex-1`) para que el centro
                quede centrado DE VERDAD: con la izquierda dimensionada por su
                contenido, la navegación se desplazaría según lo que hubiera que
                avisar. */}
            {/* En móvil se APILA: la navegación primero y los avisos debajo.
                Con tres columnas en una pantalla estrecha, la de la izquierda se
                quedaba en cuarenta píxeles y «Arrastra una cita para moverla»
                salía en vertical, letra a letra. Visto en un móvil de verdad. */}
            <div className="flex flex-col-reverse items-stretch gap-2 lg:flex-row lg:items-start lg:gap-4">
                <div className="flex min-w-0 flex-col gap-0.5 text-xs text-neutral-500 lg:flex-1 lg:pt-1">
                    {isPending ? (
                        <span>Consultando la agenda…</span>
                    ) : (
                        isFetching && <span className="text-neutral-600">actualizando…</span>
                    )}
                    {isError && <span className="text-rose-400">No se pudo consultar la agenda.</span>}

                    <span>Arrastra una cita para moverla. En móvil, «mover».</span>

                    {data?.agenda.alcanzable && (
                        <>
                            {citasVisibles.length === 0 && vista !== 'mes' && (
                                <span>
                                    Ninguna cita este día.{hayFiltro && ' Hay filtros puestos.'}
                                </span>
                            )}
                            {/* FR-002 · un horario inventado se ve igual que uno
                                bueno, así que se dice. */}
                            {!horario.delNegocio && vista !== 'mes' && (
                                <span className="text-amber-400">Sin horario del negocio: se enseña 8–21.</span>
                            )}
                            {data.laboratorio.citas > 0 &&
                                (data.laboratorio.incluidas ? (
                                    <span className="text-amber-400">
                                        🧪 {data.laboratorio.citas} de estas citas las creó el laboratorio.
                                    </span>
                                ) : (
                                    <span className="text-neutral-600">
                                        Se ocultan {data.laboratorio.citas} citas del laboratorio.
                                    </span>
                                ))}
                            {!data.bloqueos.alcanzable && (
                                <span className="text-neutral-600">
                                    No se han podido leer los huecos retenidos por el asistente.
                                </span>
                            )}
                            {/* La agenda devolvió su tope: puede haber citas sin
                                traer, y un hueco que parece libre y no lo está es
                                lo peor que enseña un calendario. */}
                            {data.incompleto && (
                                <span className="text-amber-400">
                                    La agenda devolvió su máximo: pueden faltar citas. Acota el rango.
                                </span>
                            )}
                            {data.recortado && (
                                <span className="text-amber-400">
                                    Se pidió más rango del que se sirve: hasta el {data.hasta}.
                                </span>
                            )}
                            {propuestas.size > 0 && (
                                <span className="text-amber-300">
                                    {propuestas.size} cambio{propuestas.size > 1 ? 's' : ''} sin guardar.
                                </span>
                            )}
                            {avisoCrear && <span className="text-amber-300">{avisoCrear}</span>}
                            {/* FR-028 · si una falla se dice cuál y por qué. */}
                            {fallos.length > 0 && (
                                <span className="text-rose-400">
                                    No se pudo mover {fallos.length === 1 ? 'una cita' : `${fallos.length} citas`}:{' '}
                                    {fallos.map((f) => f.motivo).join(' · ')}
                                </span>
                            )}
                        </>
                    )}
                </div>

                <NavegacionCalendario
                    dia={dia}
                    setDia={setDia}
                    vista={vista}
                    setVista={setVista}
                    reservas={totalVigentes}
                    incluirPasadas={incluirPasadas}
                />

                {/* El espejo de la izquierda, solo cuando hay tres columnas: es lo
                    que hace que la navegación quede en el centro de la pantalla y
                    no en el centro de lo que sobra. */}
                <div className="hidden lg:block lg:flex-1" />
            </div>

            <FilaProfesionalesCalendario
                profesionales={data?.profesionales ?? []}
                ocultos={profesOcultos}
                alAlternar={(id) => setProfesOcultos((s) => alternar(s, id))}
                alVerTodos={() => setProfesOcultos(new Set())}
                vigentesPorProf={vigentesPorProf}
            />

            {/* Esto SÍ sustituye a la rejilla, y por eso va fuera de la banda: si
                la agenda no responde no se dibuja ningún horario, porque un
                calendario vacío sería indistinguible de un día sin citas. */}
            {data && !data.agenda.alcanzable && (
                <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-6 text-center">
                    <p className="text-sm font-medium text-amber-300">No se ha podido leer la agenda</p>
                    <p className="mt-1 text-xs text-amber-200/70">{data.agenda.motivo}</p>
                    <p className="mt-3 text-[11px] text-neutral-500">
                        No se muestra ningún horario: un calendario vacío sería indistinguible de un día sin citas.
                    </p>
                </div>
            )}

            {data?.agenda.alcanzable && (
                <>
                    {vista === 'mes' ? (
                        <CalendarioMes
                            accountId={accountId!}
                            dia={dia}
                            citas={citasVisibles}
                            nombreServ={nombreServ}
                            nombreProf={nombreProf}
                            colorDe={colorDe}
                            diaCerrado={diaCerrado}
                            ocultarCerrados={ocultarCerrados}
                            alElegirDia={(d) => {
                                setDia(d)
                                setVista('dia')
                            }}
                            {...(soloLectura
                                ? {}
                                : {
                                      alProponer: (pr: Parameters<typeof conPropuesta>[1]) => {
                                          setFallos([])
                                          setPropuestas((m) => conPropuesta(m, pr))
                                      },
                                  })}
                        />
                    ) : columnas.length === 0 ? (
                        <p className="rounded-lg border border-neutral-800 bg-neutral-900/40 px-4 py-8 text-center text-sm text-neutral-400">
                            Esta semana el centro está cerrado.
                        </p>
                    ) : (
                        <RejillaCalendario
                            accountId={accountId!}
                            dia={dia}
                            columnas={columnas}
                            horario={horario}
                            nombreProf={nombreProf}
                            nombreServ={nombreServ}
                            colorDe={colorDe}
                            duracionAlPinchar={soloLectura ? undefined : duracionParaCrear}
                            alPinchar={
                                soloLectura
                                    ? undefined
                                    : (clave, hhmm) => {
                                          if (servicioParaCrear === null || duracionParaCrear === undefined) return
                                          const col = columnas.find((c) => c.clave === clave)
                                          const profId =
                                              col?.profesionalId ??
                                              (profesVisibles.length === 1 ? (profesVisibles[0]?.id ?? null) : null)
                                          setAvisoCrear(null)
                                          setHueco({
                                              dia: col?.dia ?? dia,
                                              hhmm,
                                              profesionalId: profId,
                                              servicioId: servicioParaCrear,
                                              minutos: duracionParaCrear,
                                          })
                                      }
                            }
                            alProponer={
                                soloLectura
                                    ? undefined
                                    : (pr) => {
                                          setFallos([])
                                          setPropuestas((m) => conPropuesta(m, pr))
                                      }
                            }
                        />
                    )}
                </>
            )}
        </div>
    )
}
